import { useMutation } from '@apollo/client';
import {
  ALL_MODULES,
  COMMON_CONSTANTS,
  EventEmitter,
  TICKET_SOURCE,
  getTranslatedText,
  useActivityTracker,
  useIntl,
  useUserMSPCheckHook,
  withErrorBoundary
} from '@nile/components/src';
import {
  EventEmitterWithIndex,
  getCurrentUserHook,
  getNotificationTemplateByConstantHook
} from '@nile/components/src/Utils/helpers';
import {
  GET_DEFAULT_REPLY_TEMPLATE,
  GET_TICKET_AUDIT_LIST,
  SAVE_TICKET_CONVERSATION,
  SAVE_TICKET_INTEGRATION_CONVERSATION,
  TicketClient,
  useCachedQuery
} from '@nile/graphql';
import { GET_TICKET_REPLY_RECIPEINTS } from '@nile/graphql/src/Query/ticket';
import envConfig from '@nile/home/src/Config/env';
import { ActionContainerV2 } from '@nile/mangifera/src/Containers/ChatContainers';
import { useGlobalActionContainerStore } from '@nile/mangifera/src/StoreManager/store/actionContainerStore';
import { useTicketDetailsConversationStore } from '@nile/mangifera/src/StoreManager/store/ticketDetailsConversationStore';
import { SVGIcon } from '@superops/super-icons';
import { isEmpty } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSuperEditor } from '@nile/components/src/Components/RichTextEditor/useSuperEditor.tsx';
import { AI_AGENTS_CONSTANT_FEATURE_ID_MAP } from '@nile/components/src/Components/AI/helpers/constants';

const resolveAndCloseKeys = {
  close: 'ticket.details.conversation.sendAndClose',
  resolve: 'ticket.details.conversation.sendAndResolve'
};

const DEFAULT_TEMPLATE = {
  name: '',
  content: '',
  placeholdersUsed: '',
  mentions: [],
  attachments: [],
  inlineImages: []
};

const ConversationEditorContainer = (props) => {
  // TODO: NEED TO HANDLE ATTACHMENTS FROM REPLY TEMPLATE
  const isMSP = useUserMSPCheckHook();
  const intl = useIntl();
  const {
    ticket,
    resetConversationMode,
    statusConfigData,
    allStatuses,
    isStatusAuthorised,
    isEditAuthorised,
    conversationOrder,
    status,
    updateTicketStatus,
    focusLastAddedMessage,
    concurrentUsersTyping,
    checkHideGlobalTicketConversationCloseModal,
    checkIfActionToolbarDisabled,
    openTicketFromGlobalConversation,
    prompts,
    sendAIFeatureFeedback,
    imConfig
  } = props;

  const editorConfig = {
    id: ticket?.ticketId,
    name: `conversation_${ticket?.ticketId}`,
    cacheConfig: {
      scope: 'global'
    }
  };

  const { template, updateTemplate } = useSuperEditor(editorConfig);

  const currentUser = getCurrentUserHook();

  const activeTabIndex = useTicketDetailsConversationStore(
    (s) => s.activeTabIndex
  );

  const setIsConversationSentFromTriageAgent = useTicketDetailsConversationStore(
    (s) => s.setIsConversationSentFromTriageAgent
  );

  const isConversationSentFromTriageAgent = useTicketDetailsConversationStore(
    (s) => s.isConversationSentFromTriageAgent
  );

  const setConversationMode = useTicketDetailsConversationStore(
    (s) => s.setConversationMode
  );

  const defaultTemplate = useTicketDetailsConversationStore(
    (s) => s.defaultReplyTemplate
  );

  const setDefaultTemplate = useTicketDetailsConversationStore(
    (s) => s.setDefaultReplyTemplate
  );

  // Now we'll have 2 cases
  // 1. No content where the default reply content is got from the API and triage content is set
  // 2. Content where the default reply content and we'll insert using the event emitter
  const triageContent = useTicketDetailsConversationStore(
    (s) => s.triageContent
  );

  const setTriageContent = useTicketDetailsConversationStore(
    (s) => s.setTriageContent
  );

  const insertTriageContent = (prevContent) => {
    let content = '';

    if (triageContent && prevContent) {
      content = `${triageContent} <p><br></p><p><br></p>${prevContent}`;
    } else if (triageContent) {
      content = triageContent;
    } else if (prevContent) {
      content = prevContent;
    }

    setTriageContent(null);
    return content;
  };

  const triageContentInsertTimeOutRef = React.useRef(null);
  // Adding timeout for froala to initialize
  const triageContentWithEventEmitter = () => {
    if (triageContent) {
      if (triageContentInsertTimeOutRef.current) {
        clearTimeout(triageContentInsertTimeOutRef.current);
      }
      triageContentInsertTimeOutRef.current = setTimeout(() => {
        EventEmitterWithIndex.dispatch('insertEditorContent', triageContent);
        setTriageContent(null);
      }, 500);
    }
  };

  const getDefaultEmailSignature = () => {
    if (!isMSP && currentUser?.emailSignature) {
      return `<p><br></p><p><br></p>${currentUser?.emailSignature}`;
    }
    return null;
  };

  const defaultEmailSignature = getDefaultEmailSignature();

  const getEmailSignature = (emailSignatureContent) => {
    const emailSignatureData = emailSignatureContent
      ? emailSignatureContent.replace(/[\u200B-\u200D\uFEFF]/g, '')
      : null; // Regex to remove &#xfeff in froala editor

    return emailSignatureData;
  };

  const { data: recipientsData, refetch: refetchRecipients } = useCachedQuery(
    GET_TICKET_REPLY_RECIPEINTS,
    {
      skip: !isMSP || !isEditAuthorised,
      variables: {
        ticketInput: {
          ticketId: ticket?.ticketId
        }
      }
    }
  );

  const refreshRecipients = () => {
    if (isMSP) {
      refetchRecipients();
    }
  };

  const { loading: defaultTemplateLoading } = useCachedQuery(
    GET_DEFAULT_REPLY_TEMPLATE,
    {
      client: TicketClient,
      skip: !isMSP || !isEmpty(template),
      variables: {
        input: {
          ticket: {
            ticketId: ticket?.ticketId
          }
        }
      },
      onCompleted(res) {
        const attachmentsFromReplyTemplate = isMSP
          ? res?.getConversationContentFromReplyTemplate?.attachments || null
          : null;
        const emailSignatureData = getEmailSignature(
          res?.getConversationContentFromReplyTemplate?.content ||
            defaultEmailSignature
        );
        if (emailSignatureData || attachmentsFromReplyTemplate) {
          // Case 1: inserting content along with the default reply template
          let content = emailSignatureData;
          if (triageContent) {
            content = insertTriageContent(emailSignatureData);
          }
          setDefaultTemplate({
            content: content,
            attachments: attachmentsFromReplyTemplate
          });
          updateTemplate({
            content: content,
            attachments: attachmentsFromReplyTemplate
          });
        }
      }
    }
  );

  const recipients =
    recipientsData?.getTicketConversationReplyRecipients || null;

  const notificationTemplateByConstant = getNotificationTemplateByConstantHook({
    notificationTemplateType: 'SYSTEM_TICKETING',
    constant: 'TICKET_REQ_CLOSED'
  });

  const getStatusIcon = (type) => {
    switch (type) {
      case 'close':
        return (
          <div className="mr-8">
            <SVGIcon name="Power" />
          </div>
        );
      case 'resolve':
        return (
          <div className="mr-8">
            <SVGIcon name="Resolution" />
          </div>
        );
      default:
        return (
          <div className="mr-8">
            <SVGIcon name="TicketOutline" />
          </div>
        );
    }
  };

  const getAllStatuses = () => {
    const resolveStateName = statusConfigData?.find(
      (config) => config.configuration === 'RESOLVE'
    )?.status?.name;
    const closeStateName = statusConfigData?.find(
      (config) => config.configuration === 'CLOSE'
    )?.status?.name;

    let statuses = [];

    if (allStatuses && statusConfigData) {
      let statusData = allStatuses;
      const others = statusData.filter(
        (status) => ![resolveStateName, closeStateName].includes(status.name)
      );

      statusData = [
        ...others,
        { name: resolveStateName, default: 'resolve' },
        { name: closeStateName, default: 'close' }
      ];

      statuses = statusData.map((status) => {
        return {
          name: status.name,
          statusType: status.default,
          content: (
            <div className="flex center">
              {getStatusIcon(status.default || 'others')}
              <FormattedMessage
                id={`${resolveAndCloseKeys[status.default] ||
                  'ticket.details.conversation.sendAndStatus'}`}
                values={{ status: status.name }}
              />
            </div>
          )
        };
      });
    }

    return statuses;
  };

  const saveConversationQuery =
    ticket?.source === TICKET_SOURCE.Communication
      ? SAVE_TICKET_INTEGRATION_CONVERSATION
      : SAVE_TICKET_CONVERSATION;

  const editorRef = React.useRef(null);

  const [
    saveTicketConversation,
    { loading: newConversationLoading, error: newConversationError }
  ] = useMutation(saveConversationQuery, {
    onCompleted({ saveTicketConversation }) {
      // toast('success', 'Conversation added successfully');
      useActivityTracker({ event: 'Ticket - Sending replies on tickets' });
      focusLastAddedMessage();
      if (props?.setFailureConversationData) {
        props?.setFailureConversationData(null);
      }
    },
    update(cache, { data }) {
      const conversation = data?.saveTicketConversation;
      if (!conversation) return;
      const newFeed = {
        __typename: 'TicketAudit',
        id: conversation.conversationId,
        type: 'CONVERSATION',
        time: conversation.time,

        user: {
          userId: currentUser?.userId,
          name: currentUser?.name,
          firstName: currentUser?.firstName,
          lastName: currentUser?.lastName,
          email: currentUser?.email
        },

        conversation: {
          __typename: 'Conversation',
          content: conversation.content,
          time: conversation.time,
          user: conversation.user,
          type: conversation.type,
          conversationId: conversation.conversationId,
          outgoingMailStatus: conversation.outgoingMailStatus,
          outgoingMailFailureReason: conversation.outgoingMailFailureReason,
          attachments: conversation.attachments ?? [],
          toUsers: conversation.toUsers ?? null,
          ccUsers: conversation.ccUsers ?? null,
          bccUsers: conversation.bccUsers ?? null
        },

        threadConversation: null,
        conversationThread: null,
        vendorConversation: null,
        note: null,
        approval: null,
        task: null
      };

      cache.modify({
        fields: {
          getTicketAuditList(existing = {}, { readField }) {
            const existingFeeds = existing.ticketAudit ?? [];
            // prevent duplicate insert (retries / optimistic)
            const alreadyExists = existingFeeds.some(
              (ref) => readField('id', ref) === newFeed.id
            );
            if (alreadyExists) return existing;

            return {
              ...existing,
              ticketAudit:
                conversationOrder === 'DESC'
                  ? [newFeed, ...existingFeeds]
                  : [...existingFeeds, newFeed]
            };
          }
        }
      });
    }
  });

  const handleOnSuccessConversation = (res) => {
    if (res?.data?.saveTicketConversation) {
      setIsConversationSentFromTriageAgent(false);
      updateTemplate(defaultTemplate || DEFAULT_TEMPLATE);
      EventEmitter.dispatch('replaceEditorContent', defaultTemplate?.content);
      if (!props?.mode) {
        resetConversationMode();
      }
    }
    refreshRecipients();
    editorRef.current?.updateState('submitActionLoading', false);
  };

  const handleGlobalChatSuccess = (status, res) => {
    if (status === 'success') {
      if (!props?.mode) {
        resetConversationMode();
      }
      refreshRecipients();
    }
  };

  const onClosureModalClosed = () => {
    editorRef.current?.updateState('submitActionLoading', false);
  };

  const insertConversationWithAttachments = ({ attachments, content }) => {
    const currentTemplate = template;
    const updatedTemplate = {
      ...currentTemplate,
      attachments: attachments,
      content: content
    };
    updateTemplate(updatedTemplate);
    EventEmitterWithIndex.dispatch('replaceEditorContent', content);
  };

  const sendConversation = (params) => {
    const fromMailbox = {
      boxId: params?.selectedMailbox?.boxId
    };

    const ccUsers = [];
    params?.ccUsers?.forEach((el) => {
      if (el?.email || el?.value) {
        ccUsers.push({
          email: el?.email || el?.value
        });
      }
    });

    const bccUsers = [];
    params?.bccUsers?.forEach((el) => {
      if (el?.email || el?.value) {
        bccUsers.push({
          email: el?.email || el?.value
        });
      }
    });

    const toUsers = [];
    recipients?.to?.forEach((el) => {
      if (el?.email || el?.value) {
        toUsers.push({
          email: el?.email || el?.value
        });
      }
    });

    const attachments = !isEmpty(params?.attachments)
      ? params?.attachments.map(({ downloadURL, ...keepAttrs }) => keepAttrs)
      : [];

    let variables = {
      ticketId: ticket?.ticketId,
      content: params?.message,
      attachments: attachments
    };

    if (ticket?.source === TICKET_SOURCE.Communication) {
      variables = {
        ...variables,
        sendMail: true
      };
      if (params?.conversationScope) {
        variables = {
          ...variables,
          conversationScope: params?.conversationScope
        };
      }
      if (params?.recipientDetails) {
        variables = {
          ...variables,
          recipientDetails: params?.recipientDetails
        };
      }
    } else {
      variables = {
        ...variables,
        toUsers: toUsers,
        ccUsers: ccUsers,
        bccUsers: bccUsers,
        hasAdditionalRecipients: recipients?.hasAdditionalRecipients
      };
      if (isMSP) {
        variables = {
          ...variables,
          fromMailbox: fromMailbox
        };
      }
    }

    if (props?.setFailureConversationData) {
      props?.setFailureConversationData(variables);
    }

    let ticketInput = {};
    let statusInput = null;
    if (params?.type && typeof params?.type === 'string') {
      statusInput = {
        status: params?.type
      };
      if (params?.suppressCloseNotification) {
        statusInput.suppressCloseNotification =
          params?.suppressCloseNotification;
      }

      ticketInput = { ...ticketInput, ...statusInput };
    }

    if (isConversationSentFromTriageAgent) {
      ticketInput = {
        ...ticketInput,
        handledByAIAgent: {
          featureId: AI_AGENTS_CONSTANT_FEATURE_ID_MAP.TICKET_TRIAGE
        }
      };
    }

    if (!isEmpty(ticketInput)) {
      variables = { ...variables, ticketInput };
    }

    saveTicketConversation({
      variables: variables
    }).then((res) => {
      if (statusInput) {
        const prevStatus = status;
        if (statusInput?.status && prevStatus !== statusInput?.status) {
          updateTicketStatus(statusInput?.status);
        }
      }
      handleOnSuccessConversation(res);
    });
  };

  const sendConversationWithTicketInput = (params) => {
    // Once the mandatoryTicket fields are filled and save is clicked
    // We'll emit and event with params containing conversationInput and ticketInput
    const conversationInput = params?.conversationInput;
    let ticketInput = params?.ticketInput;

    if (isConversationSentFromTriageAgent) {
      ticketInput = {
        ...ticketInput,
        handledByAIAgent: {
          featureId: AI_AGENTS_CONSTANT_FEATURE_ID_MAP.TICKET_TRIAGE
        }
      };
    }

    const variables = {
      ...conversationInput,
      ticketInput: ticketInput
    };

    if (props?.setClosureSaveLoading) {
      props?.setClosureSaveLoading(true);
    }
    saveTicketConversation({
      variables: variables
    })
      .then((res) => {
        // Once we get response we'll do the following
        // 1. Reset editor
        // 2. setFailureConversationData to null
        // 3. Close closure modal
        // 4. setErrorsOnClosure to null
        // 5. update the ticket with updated ticket input
        // 6. clear mailbox from global state
        if (props?.setFailureConversationData) {
          props?.setFailureConversationData(null);
        }
        if (props?.setClosureModalActive) {
          props?.setClosureModalActive(false);
        }
        if (props?.setClosureSaveLoading) {
          props?.setClosureSaveLoading(false);
        }
        if (props?.setErrorsOnClosure) {
          props?.setErrorsOnClosure(null);
        }
        if (props?.updateTicketState) {
          props?.updateTicketState(ticketInput);
        }
        handleOnSuccessConversation(res);
      })
      .catch((err) => {
        if (props?.setClosureSaveLoading) {
          props?.setClosureSaveLoading(false);
        }
      });
  };

  const addReplyTemplate = (input, onSuccess) => {
    // Check why it's not working if refetch is passed from props
    TicketClient.query({
      query: GET_DEFAULT_REPLY_TEMPLATE,
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
      variables: input
    }).then((res) => {
      if (res?.data?.getConversationContentFromReplyTemplate?.content) {
        const content =
          res?.data?.getConversationContentFromReplyTemplate?.content;
        const attachmentsToAdd =
          res?.data?.getConversationContentFromReplyTemplate?.attachments;
        onSuccess(content, attachmentsToAdd);
      }
    });
  };

  const lastThreadConversation = props?.feeds
    .filter((conv) => conv?.type === 'THREAD_CONVERSATION')
    .map((conv) => conv.threadConversation)
    .findLast((conv) => conv?.conversationScope);

  const isPrivateChannel =
    lastThreadConversation?.conversationScope === 'PRIVATE';

  useEffect(() => {
    EventEmitter.subscribe('refreshRecipients', refreshRecipients);
    EventEmitter.subscribe(
      'sendConversationWithMandatoryFields',
      sendConversationWithTicketInput
    );
    EventEmitter.subscribe('closureModalClosed', onClosureModalClosed);
    const eventIndex = EventEmitterWithIndex.subscribe(
      'insertConversationWithAttachments',
      insertConversationWithAttachments
    );
    if (isMSP && !isEmpty(template) && !isEmpty(triageContent)) {
      // Case 2: Inserting content if there is already a content
      triageContentWithEventEmitter();
    }
    return function cleanup() {
      EventEmitter.unsubscribe('refreshRecipients');
      EventEmitter.unsubscribe('sendConversationWithMandatoryFields');
      EventEmitterWithIndex.unsubscribe(
        'insertConversationWithAttachments',
        eventIndex
      );
      if (triageContentInsertTimeOutRef.current) {
        clearTimeout(triageContentInsertTimeOutRef.current);
      }
    };
  }, []);

  return (
    <div className="m15">
      <ActionContainerV2
        template={defaultTemplate || DEFAULT_TEMPLATE}
        globalChatSuccessCallBack={handleGlobalChatSuccess}
        ticket={ticket}
        convertConversationToWorklog
        placeholder={getTranslatedText(
          intl,
          'ticket.details.chat.pl.desc.convo'
        )}
        mode="conversation"
        onMessageSubmit={sendConversation}
        module="TICKET_CONVERSATION"
        details={{
          operation: 'CREATE',
          ticketId: ticket?.ticketId
        }}
        attachmentUploadUrl={`${envConfig.fileRoot}/upload`}
        uploadParams={{
          module: ALL_MODULES.ticketConversationAttachment,
          details: JSON.stringify({ ticketId: ticket.ticketId })
        }}
        isSplit={
          isStatusAuthorised && isEditAuthorised && getAllStatuses()?.length > 0
        }
        splitOptions={getAllStatuses()}
        recipients={recipients}
        refetchDefaultReplyTemplate={addReplyTemplate}
        concurrentUsersTyping={concurrentUsersTyping}
        notificationTemplateByConstant={notificationTemplateByConstant}
        sendAgentCollisionDataFromFloatingEditor={
          props?.sendAgentCollisionDataFromFloatingEditor
        }
        resetConversationMode={resetConversationMode}
        globalConversationFlag
        checkHideGlobalTicketConversationCloseModal={
          checkHideGlobalTicketConversationCloseModal
        }
        checkIfActionToolbarDisabled={checkIfActionToolbarDisabled}
        openTicketFromGlobalConversation={openTicketFromGlobalConversation}
        ticketRevampFlag
        showAIRephrase
        prompts={prompts}
        sendAIFeatureFeedback={sendAIFeatureFeedback}
        actionButtonStyles={{
          position: 'relative',
          right: '0',
          bottom: '0'
        }}
        onCancel={resetConversationMode}
        isPrivateChannel={isPrivateChannel}
        parentRef={editorRef}
        editorConfig={editorConfig}
        imConfig={imConfig}
      />
    </div>
  );
};

export default withErrorBoundary(ConversationEditorContainer);
